import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AccountInfo, AuthenticationResult, InteractionStatus } from '@azure/msal-browser';
import { catchError, filter, from, map, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { LoginClaims } from 'src/app/core/models/auth/claims.model';
import { AuthUser, Permission, ProfileUser } from 'src/app/core/models/auth/user.model';
import { AlertService } from 'src/app/core/services/alert.service';
import { UserService } from 'src/app/core/services/user.service';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-msal-login',
    templateUrl: './msal-login.component.html',
    styleUrls: ['./msal-login.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class MsalLoginComponent implements OnInit {
    private readonly _destroying$ = new Subject<void>();

    constructor(
        private router: Router,
        private msalService: MsalService,
        private msalBroadcastService: MsalBroadcastService,
        private userService: UserService,
        private alertService: AlertService
    ) {}

    ngOnInit(): void {
        this.msalBroadcastService.inProgress$
            .pipe(
                filter((status: InteractionStatus) => status === InteractionStatus.None),
                takeUntil(this._destroying$),
                switchMap(() => {
                    const accounts = this.msalService.instance.getAllAccounts();

                    if (accounts.length === 0) {
                        this.msalService.loginRedirect({ scopes: [...environment.msal_scope] });
                        return of(null);
                    }

                    const account = accounts[0];
                    this.msalService.instance.setActiveAccount(account);

                    return from(
                        this.msalService.instance.acquireTokenSilent({
                            account,
                            scopes: [...environment.msal_scope],
                        })
                    ).pipe(
                        catchError(() => {
                            this.msalService.loginRedirect({ scopes: [...environment.msal_scope] });
                            return of(null);
                        })
                    );
                }),
                switchMap((authenticationResult: AuthenticationResult | null) => {
                    if (!authenticationResult) return of(null);

                    const idTokenClaims = authenticationResult.idTokenClaims as any;
                    const nativeID = idTokenClaims?.['nativeid'] ? String(idTokenClaims?.['nativeid']) : '';

                    const authUser: AuthUser = {
                        username: nativeID,
                    };

                    const claims: LoginClaims = {
                        userName: idTokenClaims?.['nativeid'] ? String(idTokenClaims?.['nativeid']) : null,
                        fullName: idTokenClaims?.['name'] ? String(idTokenClaims?.['name']) : null,
                        email: idTokenClaims?.['preferred_username'] ? String(idTokenClaims?.['preferred_username']) : null,
                        profileName: idTokenClaims?.['preferred_username'] ? String(idTokenClaims?.['preferred_username']) : null,
                        password: '',
                    };
                    
                    this.userService.setAuth(authUser);
                    return this.userService.login(claims);
                })
            )
            .subscribe({
                next: (res: any) => {
                    if (!res) return;

                    // navigate to home page
                    let profileUser: ProfileUser = {
                        profileName: res.data.profile.profileName,
                        department: res.data.profile.department,
                        fullName: res.data.profile.fullName,
                        needChgPass: res.data.profile.needChgPass,
                        position: res.data.profile.position,
                        profilePic: res.data.profile.profilePic,
                    };
                    let permission = res.data.perm as Array<Permission>;

                    this.userService.setProfileUser(profileUser);
                    this.userService.setPermission(permission);
                    this.router.navigate(['/dashboard']);
                    this.alertService.clearAlert();
                },
                error: (error: HttpErrorResponse) => {
                    this.alertService.alertError(error.message);
                    console.error('Error during MSAL login process:', error);
                },
            });
    }
}
